import { Permission } from "./types/permissions";

// Django/library internals that are never meaningful to assign to a
// business role. Hidden by default in the permission picker.
const SYSTEM_APPS = new Set([
  "admin",
  "auth",
  "contenttypes",
  "sessions",
  "messages",
  "staticfiles",
  "humanize",
  "guardian",
  "pghistory",
  "pgtrigger",
  "taggit",
  "procrastinate",
  "sites",
  "authtoken",
  "token_blacklist",
]);

export const isSystemApp = (appLabel: string) =>
  SYSTEM_APPS.has(appLabel) || appLabel.startsWith("wagtail");

export const CRUD_VERBS = ["view", "add", "change", "delete"] as const;
export type CrudVerb = (typeof CRUD_VERBS)[number];
export const VERB_LABEL: Record<CrudVerb, string> = {
  view: "View",
  add: "Add",
  change: "Edit",
  delete: "Delete",
};

export interface ModelGroup {
  model: string;
  isEventTwin: boolean;
  crud: Partial<Record<CrudVerb, Permission>>;
  other: Permission[];
  all: Permission[];
}

export const humanize = (s: string) =>
  s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ");

export const modelPermIds = (m: ModelGroup) => m.all.map((p) => p.id);
export const modelsPermIds = (models: ModelGroup[]) =>
  models.flatMap(modelPermIds);

/**
 * Groups permissions by app_label, then by model, splitting each model's
 * permissions into the 4 standard CRUD verbs plus any custom ones. Models
 * are flagged as "event twins" (django-pghistory audit trail) when a
 * sibling model in the same app exists with the "event" suffix removed.
 */
export function buildGroupedByApp(
  allPermissions: Permission[],
): Record<string, ModelGroup[]> {
  const byApp: Record<string, Permission[]> = {};
  if (Array.isArray(allPermissions)) {
    allPermissions.forEach((p) => {
      const app = p.content_type.app_label;
      if (!byApp[app]) byApp[app] = [];
      byApp[app].push(p);
    });
  }

  const result: Record<string, ModelGroup[]> = {};
  Object.entries(byApp).forEach(([app, perms]) => {
    const byModel: Record<string, Permission[]> = {};
    perms.forEach((p) => {
      const model = p.content_type.model;
      if (!byModel[model]) byModel[model] = [];
      byModel[model].push(p);
    });

    const modelNames = new Set(Object.keys(byModel));
    result[app] = Object.entries(byModel).map(([model, modelPerms]) => {
      const crud: Partial<Record<CrudVerb, Permission>> = {};
      const other: Permission[] = [];
      modelPerms.forEach((p) => {
        const verb = CRUD_VERBS.find((v) => p.codename === `${v}_${model}`);
        if (verb) {
          crud[verb] = p;
        } else {
          other.push(p);
        }
      });

      const isEventTwin =
        model.endsWith("event") && modelNames.has(model.slice(0, -5));

      return { model, isEventTwin, crud, other, all: modelPerms };
    });
  });
  return result;
}

/** Map of permission id -> app_label, for cheap per-role stat lookups. */
export function buildPermissionAppMap(
  allPermissions: Permission[],
): Map<number, string> {
  const map = new Map<number, string>();
  allPermissions.forEach((p) => map.set(p.id, p.content_type.app_label));
  return map;
}

export function countDistinctApps(
  permissionIds: number[],
  appMap: Map<number, string>,
): number {
  const apps = new Set<string>();
  permissionIds.forEach((id) => {
    const app = appMap.get(id);
    if (app) apps.add(app);
  });
  return apps.size;
}
